import { Form, Input, Select, Flex, Row, Col, Tabs, Upload, Dropdown, Grid, Button, Space, Modal } from "antd";
import type { UploadFile, UploadProps } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faCopy,
  faPaste,
  faDumbbell,
  faGripVertical,
  faUpload,
  faVideo,
  faEllipsis,
  faPlus,
  faFileImport,
  faLinkSlash,
  faLink,
  faRotate,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { ExerciseEntry, ExerciseProgression } from "../../types/Programmazione";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ExerciseCard.css";
import IconButton from "../../common/IconButton";
import { Option } from "../../types/generics";
import { Draggable } from "react-beautiful-dnd";
import { useToasts } from "../../contexts/toasts";
import { useClipboard } from "../../contexts/clipboard";
import { usePrecompiledProgressionName } from "../../hooks/usePrecompiledProgressionName";
import { usePrecompiledProgressionMeta } from "../../hooks/usePrecompiledProgressionMeta";
import {
  applyLibraryUpdate,
  hasLibraryUpdate,
  mergeAttachedProgression,
  shouldPromptAttachMode,
} from "../../utils/progressionLink";
import { Entities } from "../../api/Entities";
import ExerciseProgressionEditor from "./ExerciseProgressionEditor";
import VideoThumb from "../../common/VideoThumb";
import PrecompiledProgressionPicker, {
  PrecompiledProgressionPickerResult,
} from "./PrecompiledProgressionPicker";
import type { ProgEditorOptions } from "../../hooks/useEntityOptions";

/**
 * PROG-IN-SPLIT/FE-CARD (#1153bd6b) — adds a sibling "Progressione" Collapse
 * panel that mounts <ExerciseProgressionEditor mode="trainer-edit"> and the
 * <PrecompiledProgressionPicker> modal. Three-state header (empty / library /
 * inline) drives the panel chrome. value/onChange flows through the existing
 * onFieldChange(splitIndex, exerciseIndex, "progression", next) plumbing so
 * the form sees the new field at `splits[s].exercises[i].progression`.
 *
 * The base SPLIT-LEAN/B Info-panel surface (type radio, exercise picker,
 * image/video upload, notes) is preserved verbatim — this task only adds
 * the progression panel as a SIBLING under the same Collapse.
 */

type Props = {
  exercise: ExerciseEntry;
  index: number;
  splitIndex: number;
  onDelete?: (splitIndex: number, exerciseIndex: number) => void;
  exerciseCatalog?: Option[];
  /** Muscle-group options for the per-exercise selector. */
  muscleGroupOptions?: Option[];
  onFieldChange?: (
    splitIndex: number,
    exerciseIndex: number,
    field: keyof ExerciseEntry,
    value: any,
  ) => void;
  onOverwrite?: (splitIndex: number, exerciseIndex: number) => void;
  isEditMode?: boolean;
  /** PROG-WEEK-PARAMS/FE-1 — page-lifted catalogs piped through to the
   *  progression editor; one shared bundle replaces the per-card hook fanout. */
  progEditorOptions?: ProgEditorOptions;
};

export default function ExerciseCard({
  exercise,
  index,
  splitIndex,
  onDelete,
  exerciseCatalog,
  muscleGroupOptions,
  onFieldChange,
  onOverwrite,
  progEditorOptions,
}: Props) {
  // PROG-UX/IMPL F3 — two-tier responsive model: `isCompact` (<sm) for
  // Dropdown-overflow decisions; `isMobile` (<md) for stack/wrap decisions.
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.sm;
  const isMobile = !screens.md;
  const [activeTab, setActiveTab] = useState<string>("details");
  const [selectedExercise, setSelectedExercise] = useState<Option>();
  const { copyExercise, copiedExercise } = useClipboard();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [videoList, setVideoList] = useState<UploadFile[]>([]);
  const { showToast } = useToasts();
  const [pickerOpen, setPickerOpen] = useState(false);
  // PROG-ATTACH-MERGE — when the picker confirms on an exercise that already
  // has weeks, stash the result and ask the trainer whether to replace the
  // existing weeks or append the picked ones in coda.
  const [pendingAttach, setPendingAttach] =
    useState<PrecompiledProgressionPickerResult | null>(null);

  // Catalog exercises fetched for the currently-selected muscle group. When a
  // muscle group is chosen (catalog type), we hit the API filtered by that
  // group rather than client-filtering the preloaded full catalog. Cached per
  // muscle group so reselecting the same group doesn't refetch.
  const [groupExercises, setGroupExercises] = useState<Option[] | null>(null);
  const [loadingGroupEx, setLoadingGroupEx] = useState(false);
  const groupExCacheRef = useRef<Map<number, Option[]>>(new Map());

  useEffect(() => {
    const mgId = exercise.muscleGroupId;
    if (exercise.type !== "catalog" || mgId == null) {
      setGroupExercises(null);
      return;
    }
    const cached = groupExCacheRef.current.get(mgId);
    if (cached) {
      setGroupExercises(cached);
      return;
    }
    let active = true;
    setLoadingGroupEx(true);
    Entities.getExercises(new URLSearchParams({ muscleGroupId: String(mgId) }))
      .then(({ data }) => {
        const opts: Option[] = (data.list ?? []).map((ex) => ({
          label: ex.name,
          value: ex,
        }));
        groupExCacheRef.current.set(mgId, opts);
        if (active) setGroupExercises(opts);
      })
      .catch(() => {
        if (active) setGroupExercises([]);
      })
      .finally(() => {
        if (active) setLoadingGroupEx(false);
      });
    return () => {
      active = false;
    };
  }, [exercise.type, exercise.muscleGroupId]);

  // FE-CARD: header pill is driven by the source shape. A library source with
  // detached=true is semantically inline (the snapshot is now user-owned).
  const progression = exercise.progression;
  // Name of the source library progression (resolved from its id) so the
  // header can reference which precompiled progression this exercise uses.
  const libraryProgressionId =
    progression?.source.kind === "library"
      ? progression.source.precompiledProgressionId
      : null;
  const libraryProgressionName = usePrecompiledProgressionName(libraryProgressionId);
  const headerPillState: "none" | "library" | "inline" = useMemo(() => {
    if (!progression) return "none";
    if (progression.source.kind === "library" && !progression.source.detached) {
      return "library";
    }
    return "inline";
  }, [progression]);

  // FE-SWAP (#6903c8e3) — option A: also surface the empty CTAs when a
  // progression is attached but its weeks array is empty (the user deleted
  // every week and would otherwise be stuck staring at an empty editor with
  // no way to pick a new template). The header pill still shows so they know
  // what was previously attached.
  const hasWeeks = !!progression && (progression.weeks?.length ?? 0) > 0;
  // Show the choose-library / create-here CTAs only when there's no progression
  // yet, or a (degenerate) library progression with no weeks. An INLINE
  // progression — even with zero weeks — drops straight into the editor so the
  // trainer can build it in place (the editor offers "Aggiungi settimana").
  const showEmptyCtas =
    !progression ||
    (!hasWeeks && progression.source.kind !== "inline");

  const handleProgressionChange = useCallback(
    (next: ExerciseProgression) => {
      onFieldChange?.(splitIndex, index, "progression", next);
    },
    [onFieldChange, splitIndex, index],
  );

  const handleStartInline = useCallback(() => {
    onFieldChange?.(splitIndex, index, "progression", {
      weeks: [],
      source: { kind: "inline" },
    } satisfies ExerciseProgression);
  }, [onFieldChange, splitIndex, index]);

  const handleDetach = useCallback(() => {
    if (!progression || progression.source.kind !== "library") return;
    onFieldChange?.(splitIndex, index, "progression", {
      ...progression,
      source: { ...progression.source, detached: true },
    });
  }, [onFieldChange, splitIndex, index, progression]);

  // Collega/Scollega — re-link a previously detached library snapshot so it can
  // once again receive "aggiornamento disponibile" advisories.
  const handleRelink = useCallback(() => {
    if (!progression || progression.source.kind !== "library") return;
    onFieldChange?.(splitIndex, index, "progression", {
      ...progression,
      source: { ...progression.source, detached: false },
    });
  }, [onFieldChange, splitIndex, index, progression]);

  // Library version metadata for the linked snapshot — drives the
  // "aggiornamento disponibile" advisory and the "Aggiorna" re-sync.
  const libraryMeta = usePrecompiledProgressionMeta(libraryProgressionId);
  const isDetachedLibrary =
    progression?.source.kind === "library" && progression.source.detached === true;
  const updateAvailable = hasLibraryUpdate(progression, libraryMeta?.updatedAt);

  const handleApplyLibraryUpdate = useCallback(() => {
    if (!progression || progression.source.kind !== "library") return;
    if (!libraryMeta?.weeks) return;
    const next = applyLibraryUpdate(progression, libraryMeta.weeks, libraryMeta.updatedAt);
    onFieldChange?.(splitIndex, index, "progression", next);
    showToast(
      `progression-updated-${splitIndex}-${index}`,
      "Progressione aggiornata dalla libreria",
      "",
      "success"
    );
  }, [onFieldChange, splitIndex, index, progression, libraryMeta, showToast]);

  const handlePickerConfirm = useCallback(
    (result: PrecompiledProgressionPickerResult) => {
      setPickerOpen(false);
      // If the exercise already has configured weeks, ask replace vs append
      // instead of silently overwriting them.
      if (shouldPromptAttachMode(progression)) {
        setPendingAttach(result);
        return;
      }
      onFieldChange?.(
        splitIndex,
        index,
        "progression",
        mergeAttachedProgression(progression, result, "replace"),
      );
    },
    [onFieldChange, splitIndex, index, progression],
  );

  const resolvePendingAttach = useCallback(
    (mode: "replace" | "append") => {
      if (!pendingAttach) return;
      onFieldChange?.(
        splitIndex,
        index,
        "progression",
        mergeAttachedProgression(progression, pendingAttach, mode),
      );
      setPendingAttach(null);
    },
    [onFieldChange, splitIndex, index, progression, pendingAttach],
  );

  useEffect(() => {
    if (exercise.exerciseId && exerciseCatalog) {
      const selected = exerciseCatalog.find(
        (ex) => ex.value.id === exercise.exerciseId,
      );
      if (selected) {
        setSelectedExercise(selected);
        if (selected.value.signedUrl) {
          setFileList([
            {
              uid: "-1",
              name: selected.label,
              status: "done",
              url: selected.value.signedUrl,
            },
          ]);
        } else {
          setFileList([]);
        }
      }
    } else {
      setFileList([]);
    }
  }, [exercise.exerciseId, exerciseCatalog]);

  const handleExerciseSelect = useCallback(
    (exerciseId: number) => {
      // Resolve from the group-fetched list first (the visible source when a
      // muscle group is chosen), falling back to the full preloaded catalog.
      const selected =
        groupExercises?.find((ex) => ex.value.id === exerciseId) ??
        exerciseCatalog?.find((ex) => ex.value.id === exerciseId);
      if (selected) {
        onFieldChange?.(splitIndex, index, "exerciseId", exerciseId);
        onFieldChange?.(splitIndex, index, "customName", selected.label);
        // Keep the muscle group in sync with the chosen catalog exercise.
        if (selected.value?.muscleGroupId != null) {
          onFieldChange?.(
            splitIndex,
            index,
            "muscleGroupId",
            selected.value.muscleGroupId,
          );
        }
        setSelectedExercise(selected);
        if (selected.value.signedUrl) {
          setFileList([
            {
              uid: "-1",
              name: selected.label,
              status: "done",
              url: selected.value.signedUrl,
            },
          ]);
        } else {
          setFileList([]);
        }
      } else {
        onFieldChange?.(splitIndex, index, "customName", "");
        onFieldChange?.(splitIndex, index, "exerciseId", undefined);
        setSelectedExercise(undefined);
        setFileList([]);
      }
    },
    [exerciseCatalog, groupExercises, onFieldChange, splitIndex, index],
  );

  // Choosing a muscle group filters the catalog exercise options; for catalog
  // exercises we also clear the current pick so the trainer reselects from the
  // (now filtered) list.
  const handleMuscleGroupChange = useCallback(
    (mgId: number | undefined) => {
      onFieldChange?.(splitIndex, index, "muscleGroupId", mgId ?? undefined);
      if (exercise.type === "catalog") {
        onFieldChange?.(splitIndex, index, "exerciseId", undefined);
        onFieldChange?.(splitIndex, index, "customName", "");
        setSelectedExercise(undefined);
        setFileList([]);
      }
    },
    [onFieldChange, splitIndex, index, exercise.type],
  );

  const handleFieldChange = useCallback(
    (field: string) => (evt: any) => {
      if (field === "type" && evt !== "catalog") {
        setSelectedExercise(undefined);
        setFileList([]);
        setVideoList([]);
      }
      const val = evt && evt.target ? evt.target.value : evt;
      onFieldChange?.(splitIndex, index, field as keyof ExerciseEntry, val);
    },
    [index, splitIndex, onFieldChange],
  );

  const fileListRef = useRef(fileList);
  fileListRef.current = fileList;
  const videoListRef = useRef(videoList);
  videoListRef.current = videoList;

  const uploadProps: UploadProps = useMemo(() => ({
    onRemove: () => {
      if (fileListRef.current[0]?.url && fileListRef.current[0].url.startsWith("blob:")) {
        URL.revokeObjectURL(fileListRef.current[0].url);
      }
      onFieldChange?.(splitIndex, index, "image", null);
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isImageFile = file.type.startsWith("image/");
      if (!isImageFile) {
        showToast(
          "file-type-error",
          "Puoi caricare solo immagini",
          null,
          "warning",
        );
        return Upload.LIST_IGNORE;
      }
      if (fileListRef.current[0]?.url && fileListRef.current[0].url.startsWith("blob:")) {
        URL.revokeObjectURL(fileListRef.current[0].url);
      }
      const previewUrl = URL.createObjectURL(file);
      onFieldChange?.(splitIndex, index, "image", file);
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: previewUrl,
          originFileObj: file,
        } as UploadFile,
      ]);
      return false;
    },
    fileList,
    listType: "picture-card" as const,
    maxCount: 1,
    className: "exercise-media-upload",
  }), [fileList, onFieldChange, splitIndex, index, showToast]);

  const videoUploadProps: UploadProps = useMemo(() => ({
    onRemove: () => {
      if (videoListRef.current[0]?.url && videoListRef.current[0].url.startsWith("blob:")) {
        URL.revokeObjectURL(videoListRef.current[0].url);
      }
      onFieldChange?.(splitIndex, index, "video", null);
      setVideoList([]);
    },
    beforeUpload: (file) => {
      const isVideoFile = file.type.startsWith("video/");
      if (!isVideoFile) {
        showToast(
          "file-type-error",
          "Puoi caricare solo video",
          null,
          "warning",
        );
        return Upload.LIST_IGNORE;
      }
      if (videoListRef.current[0]?.url && videoListRef.current[0].url.startsWith("blob:")) {
        URL.revokeObjectURL(videoListRef.current[0].url);
      }
      const previewUrl = URL.createObjectURL(file);
      onFieldChange?.(splitIndex, index, "video", file);
      setVideoList([
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: previewUrl,
          originFileObj: file,
        } as UploadFile,
      ]);
      return false;
    },
    fileList: videoList,
    listType: "picture-card" as const,
    maxCount: 1,
    accept: "video/*",
    className: "exercise-media-upload",
    // picture-card renders every item as an <img>, which can't show a video —
    // so a just-inserted clip looked like a broken/static thumbnail. Render a
    // moving <video> preview (muted autoplay loop) instead, with a remove
    // action, so the animation is visible right in the anteprima.
    itemRender: (_originNode, file, _fileList, actions) => {
      const url =
        file.url ||
        (file.originFileObj
          ? URL.createObjectURL(file.originFileObj as File)
          : "");
      return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {url && (
            <VideoThumb
              src={url}
              radius={8}
              style={{ width: "100%", height: "100%" }}
              onClick={() =>
                window.open(url, "_blank", "noopener,noreferrer")
              }
              title={file.name}
            />
          )}
          <Button
            type="text"
            size="small"
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => actions.remove()}
            aria-label="Rimuovi video"
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              color: "#fff",
              background: "rgba(0, 0, 0, 0.5)",
            }}
          />
        </div>
      );
    },
  }), [videoList, onFieldChange, splitIndex, index, showToast]);

  // PROG-BUG/CATALOG-LABEL — derive the catalog Select's options so the
  // current `exerciseId` ALWAYS resolves to a label, even if:
  //   (a) the catalog hasn't loaded yet on Edit-page mount (race with
  //       Entities.getExercises() — Form is hydrated first; without this
  //       synthetic option the Select renders the raw numeric ID until
  //       the catalog arrives, which the user reported as a visible bug);
  //   (b) the saved `exerciseId` refers to a catalog entry that has since
  //       been removed (stale row). Falls back to `customName` if the BE
  //       stored it alongside, else a generic placeholder including the id.
  const catalogSelectOptions = useMemo(() => {
    // When a muscle group is chosen we list the API-fetched exercises for that
    // group; otherwise the full preloaded catalog. A synthetic option keeps the
    // current exerciseId's label visible even before its source list arrives.
    const mgId = exercise.muscleGroupId;
    const source: Option[] =
      mgId != null ? groupExercises ?? [] : exerciseCatalog ?? [];
    const base = source.map((ex) => ({
      label: ex.label,
      value: ex.value.id,
    }));
    const id = exercise.exerciseId;
    if (id == null) return base;
    const hasMatch = base.some((o) => o.value === id);
    if (hasMatch) return base;
    const fallbackLabel =
      exercise.customName?.trim() || `Esercizio #${id}`;
    return [{ label: fallbackLabel, value: id }, ...base];
  }, [exerciseCatalog, groupExercises, exercise.exerciseId, exercise.customName, exercise.muscleGroupId]);

  const dettagliContent = useMemo(
    () => (
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {/* PROG-UX/IDENT-ROW — Tipo + Gruppo Muscolare + Esercizio. On sm+ they
            sit side-by-side (8/8/8); on <sm they stack. The muscle group is
            chosen first and filters the catalog exercise options. */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <label className="fw-bold d-block mb-2">Tipo</label>
            <Form.Item
              noStyle
              name={["splits", splitIndex, "exercises", index, "type"]}
            >
              <Select
                placeholder="Seleziona tipo"
                className="custom-input w-100"
                onChange={handleFieldChange("type")}
                allowClear
                defaultValue="custom"
                options={[
                  { label: "Catalogo", value: "catalog" },
                  { label: "Personalizzato", value: "custom" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <label className="fw-bold d-block mb-2">Gruppo Muscolare</label>
            <Form.Item
              noStyle
              name={["splits", splitIndex, "exercises", index, "muscleGroupId"]}
            >
              <Select
                placeholder="Gruppo muscolare"
                className="custom-input w-100"
                onChange={handleMuscleGroupChange}
                allowClear
                showSearch
                optionFilterProp="label"
                options={muscleGroupOptions ?? []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <label className="fw-bold d-block mb-2">Esercizio</label>
            {exercise.type === "catalog" ? (
              <Form.Item
                name={["splits", splitIndex, "exercises", index, "exerciseId"]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  placeholder="Seleziona esercizio..."
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  onChange={handleExerciseSelect}
                  className="custom-input"
                  loading={loadingGroupEx}
                  options={catalogSelectOptions}
                  notFoundContent={
                    <div className="p-2 text-center justify-content-center align-items-center d-flex flex-column">
                      <FontAwesomeIcon
                        icon={faDumbbell}
                        size="2x"
                        className="mb-2"
                      />
                      <span>
                        {loadingGroupEx ? "Caricamento…" : "Nessun esercizio"}
                      </span>
                    </div>
                  }
                />
              </Form.Item>
            ) : (
              <Form.Item
                name={["splits", splitIndex, "exercises", index, "customName"]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  size="large"
                  placeholder="Esercizio personalizzato"
                  className="custom-input"
                  onChange={(e) => onFieldChange?.(splitIndex, index, "customName", e.target.value)}
                />
              </Form.Item>
            )}
          </Col>
        </Row>
        <div className="exercise-subsection-title fw-bold text-uppercase small text-muted">
          Media
        </div>
        <Row gutter={[16, 16]} className="exercise-media-row">
          <Col xs={24} md={12}>
            <label className="fw-bold d-block mb-2">Immagine</label>
            <Upload {...uploadProps}>
              <Flex
                vertical
                align="center"
                justify="center"
                gap={6}
                className="exercise-media-upload__trigger"
              >
                <FontAwesomeIcon icon={faUpload} size="lg" />
                <span className="exercise-media-upload__label">Carica immagine</span>
                <span className="exercise-media-upload__hint">PNG, JPG</span>
              </Flex>
            </Upload>
          </Col>
          <Col xs={24} md={12}>
            <label className="fw-bold d-block mb-2">Video Esecuzione</label>
            <Upload {...videoUploadProps}>
              <Flex
                vertical
                align="center"
                justify="center"
                gap={6}
                className="exercise-media-upload__trigger"
              >
                <FontAwesomeIcon icon={faVideo} size="lg" />
                <span className="exercise-media-upload__label">Carica video</span>
                <span className="exercise-media-upload__hint">MP4, MOV</span>
              </Flex>
            </Upload>
          </Col>
        </Row>
        <div className="exercise-subsection-title fw-bold text-uppercase small text-muted">
          Note
        </div>
        <Form.Item
          name={["splits", splitIndex, "exercises", index, "notes"]}
          style={{ marginBottom: 0 }}
        >
          <Input.TextArea
            placeholder="Note..."
            className="custom-input"
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </Form.Item>
      </Space>
    ),
    [
      exercise.type,
      splitIndex,
      index,
      handleFieldChange,
      handleExerciseSelect,
      handleMuscleGroupChange,
      muscleGroupOptions,
      catalogSelectOptions,
      loadingGroupEx,
      uploadProps,
      videoUploadProps,
      onFieldChange,
    ],
  );

  const handleDeleteItem = useCallback(
    (splitIdx: number, exIdx: number) => () => {
      onDelete?.(splitIdx, exIdx);
    },
    [onDelete],
  );

  const ExerciseTitle = useCallback(() => {
    const displayName =
      exercise.type === "catalog" && selectedExercise
        ? selectedExercise.label
        : exercise.customName || `Esercizio ${index + 1}`;

    return (
      <Flex justify="space-between" align="center" className="w-100">
        <div className="d-flex align-items-center">
          <small className="exercise-number me-3 bg-info">{index + 1}</small>
          <strong>{displayName}</strong>
        </div>
        <Flex align="center" gap={isCompact ? 4 : 8}>
          {isCompact ? (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "copia",
                    label: "Copia Esercizio",
                    icon: <FontAwesomeIcon icon={faCopy} />,
                    onClick: () => copyExercise(exercise),
                  },
                  ...(copiedExercise && onOverwrite
                    ? [{
                        key: "incolla",
                        label: "Incolla qui (sovrascrive)",
                        icon: <FontAwesomeIcon icon={faPaste} />,
                        onClick: () => onOverwrite(splitIndex, index),
                      }]
                    : []),
                ],
              }}
            >
              <IconButton
                tooltip="Azioni"
                onClick={(e: any) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faEllipsis} className="cursor-pointer" />
              </IconButton>
            </Dropdown>
          ) : (
            <Flex align="center" gap={4}>
              <IconButton
                onClick={(e) => { e.stopPropagation(); copyExercise(exercise); }}
                tooltip="Copia Esercizio"
              >
                <FontAwesomeIcon icon={faCopy} className="cursor-pointer" />
              </IconButton>
              {copiedExercise && onOverwrite && (
                <IconButton
                  onClick={(e) => { e.stopPropagation(); onOverwrite(splitIndex, index); }}
                  tooltip="Incolla qui (sovrascrive)"
                >
                  <FontAwesomeIcon icon={faPaste} className="cursor-pointer" />
                </IconButton>
              )}
            </Flex>
          )}
          <IconButton
            onClick={handleDeleteItem(splitIndex, index)}
            tooltip="Rimuovi Esercizio"
          >
            <FontAwesomeIcon
              icon={faTrash}
              className="cursor-pointer text-danger"
            />
          </IconButton>
        </Flex>
      </Flex>
    );
  }, [exercise, selectedExercise, handleDeleteItem, index, splitIndex, copyExercise, copiedExercise, onOverwrite, isCompact]);

  // ─── FE-TABS: Progressione tab ────────────────────────────────────────────
  // The three-state pill + Scollega chrome now lives at the TOP of the
  // Progressione tab content (was the Collapse panel label pre-FE-TABS).
  // `progression-panel-header-<s>-<i>` testid is preserved here so the
  // existing FE-CARD test still finds something to click; the test now
  // clicks the tab label (see tabItems below) which switches to this tab.

  const progressionHeaderRow = useMemo(() => {
    // D10 — drop redundant `<strong>Progressione</strong>` (tab label
    // already says "Progressione"). Keep just the state pill + Scollega.
    // D6 — Scollega uses `type="link"` so it reads as a secondary action.
    // FE-SWAP (#6903c8e3) — "Cambia" link next to Scollega lets trainers
    // swap a library-attached progression for another one without first
    // having to clear it. For inline progressions it isn't shown (the
    // empty-state CTAs handle that path once weeks are cleared, and we
    // don't want to encourage destroying user-authored work via a header
    // link). For empty progressions (no source) the row collapses to null.
    if (headerPillState === "none") return null;
    return (
      <Flex
        align="center"
        justify="space-between"
        style={{ width: "100%", marginBottom: 12 }}
      >
        <Space wrap>
          {headerPillState === "library" &&
            progression?.source.kind === "library" && (
              <>
                <span
                  className="cm-tag cm-tag--brand"
                  data-testid={`progression-pill-library-${splitIndex}-${index}`}
                >
                  Da libreria
                  {progression.source.libraryVersion
                    ? ` (v${progression.source.libraryVersion.slice(0, 10)})`
                    : ""}
                </span>
                {libraryProgressionName && (
                  <span
                    className="ec-progression-ref"
                    data-testid={`progression-name-${splitIndex}-${index}`}
                  >
                    {libraryProgressionName}
                  </span>
                )}
              </>
            )}
          {headerPillState === "inline" && (
            <span
              className="cm-tag cm-tag--accent"
              data-testid={`progression-pill-inline-${splitIndex}-${index}`}
            >
              {isDetachedLibrary ? "Scollegata" : "Personalizzata"}
            </span>
          )}
          {updateAvailable && (
            <span
              className="cm-tag cm-tag--super"
              data-testid={`progression-update-available-${splitIndex}-${index}`}
            >
              <FontAwesomeIcon icon={faRotate} />
              Aggiornamento disponibile
            </span>
          )}
        </Space>
        <Space size={0} wrap>
          {headerPillState === "inline" && !isDetachedLibrary && (
            <Button
              type="link"
              icon={<FontAwesomeIcon icon={faFileImport} />}
              onClick={() => setPickerOpen(true)}
              data-testid={`progression-attach-more-${splitIndex}-${index}`}
            >
              Collega progressione
            </Button>
          )}
          {updateAvailable && (
            <Button
              type="link"
              icon={<FontAwesomeIcon icon={faRotate} />}
              onClick={handleApplyLibraryUpdate}
              data-testid={`progression-apply-update-${splitIndex}-${index}`}
            >
              Aggiorna
            </Button>
          )}
          {headerPillState === "library" && (
            <Button
              type="link"
              icon={<FontAwesomeIcon icon={faFileImport} />}
              onClick={() => setPickerOpen(true)}
              data-testid={`progression-swap-${splitIndex}-${index}`}
            >
              Cambia
            </Button>
          )}
          {headerPillState === "library" && (
            <Button
              type="link"
              icon={<FontAwesomeIcon icon={faLinkSlash} />}
              onClick={handleDetach}
              data-testid={`progression-detach-${splitIndex}-${index}`}
            >
              Scollega
            </Button>
          )}
          {isDetachedLibrary && (
            <Button
              type="link"
              icon={<FontAwesomeIcon icon={faLink} />}
              onClick={handleRelink}
              data-testid={`progression-relink-${splitIndex}-${index}`}
            >
              Collega
            </Button>
          )}
        </Space>
      </Flex>
    );
  }, [
    headerPillState,
    progression,
    splitIndex,
    index,
    handleDetach,
    handleRelink,
    handleApplyLibraryUpdate,
    isDetachedLibrary,
    updateAvailable,
  ]);

  const progressionContent = useMemo(() => {
    const body = showEmptyCtas ? (
      <Flex
        vertical
        align="center"
        gap="middle"
        data-testid={`progression-empty-${splitIndex}-${index}`}
        style={{ padding: "16px 0" }}
      >
        <span className="text-muted text-center">
          {progression && !hasWeeks
            ? "Questa progressione non ha settimane configurate. Scegline una dalla libreria o ricrea da zero."
            : "Nessuna progressione configurata per questo esercizio."}
        </span>
        {/* D7 — vertical Space on mobile so long Italian labels don't clip. */}
        <Space
          direction={isMobile ? "vertical" : "horizontal"}
          style={{ width: isMobile ? "100%" : undefined }}
          size={8}
        >
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faFileImport} />}
            onClick={() => setPickerOpen(true)}
            block={isMobile}
            data-testid={`progression-pick-library-${splitIndex}-${index}`}
          >
            Scegli progressione precompilata
          </Button>
          <Button
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={handleStartInline}
            block={isMobile}
            data-testid={`progression-start-inline-${splitIndex}-${index}`}
          >
            Crea progressione qui
          </Button>
        </Space>
      </Flex>
    ) : (
      <div data-testid={`progression-attached-${splitIndex}-${index}`}>
        <ExerciseProgressionEditor
          value={progression!}
          onChange={handleProgressionChange}
          mode="trainer-edit"
          options={progEditorOptions}
        />
      </div>
    );
    return (
      <div>
        {progressionHeaderRow}
        {body}
      </div>
    );
  }, [
    showEmptyCtas,
    hasWeeks,
    progression,
    splitIndex,
    index,
    handleStartInline,
    handleProgressionChange,
    progressionHeaderRow,
    isMobile,
    progEditorOptions,
  ]);

  const tabItems = useMemo(
    () => [
      {
        key: "details",
        label: (
          <span data-testid={`details-tab-${splitIndex}-${index}`}>
            <FontAwesomeIcon icon={faDumbbell} className="me-2" />
            Identità
          </span>
        ),
        children: dettagliContent,
      },
      {
        key: "progression",
        // The testid lives on the tab label so the existing FE-CARD test's
        // `click(progression-panel-header-...)` switches to this tab.
        label: (
          <span data-testid={`progression-panel-header-${splitIndex}-${index}`}>
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            Progressione
          </span>
        ),
        children: progressionContent,
      },
    ],
    [dettagliContent, progressionContent, splitIndex, index],
  );

  return (
    <Draggable draggableId={`exercise-${splitIndex}-${index}`} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="d-flex align-items-start mb-3"
        >
          <div {...provided.dragHandleProps} className="drag-handle me-2 pt-2">
            <FontAwesomeIcon icon={faGripVertical} />
          </div>
          <div className="exercise-card flex-grow-1">
            <div className="px-3 pt-2 pb-2">
              <ExerciseTitle />
            </div>
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key)}
              items={tabItems}
              className="exercise-card-tabs px-3"
              // PROG-DETAIL/IMPL O1 — at <sm the two-tab nav is tight on
              // viewports under 360px; shrink the bar and center the
              // labels so they balance visually.
              size={isCompact ? "small" : "middle"}
              centered={isCompact}
              tabBarStyle={isCompact ? { marginBottom: 8 } : undefined}
            />
          </div>
          <PrecompiledProgressionPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onConfirm={handlePickerConfirm}
          />
          <Modal
            open={!!pendingAttach}
            onCancel={() => setPendingAttach(null)}
            title="Collega progressione"
            data-testid={`progression-attach-mode-${splitIndex}-${index}`}
            footer={[
              <Button
                key="append"
                onClick={() => resolvePendingAttach("append")}
                data-testid={`progression-attach-append-${splitIndex}-${index}`}
              >
                Aggiungi in coda
              </Button>,
              <Button
                key="replace"
                type="primary"
                danger
                onClick={() => resolvePendingAttach("replace")}
                data-testid={`progression-attach-replace-${splitIndex}-${index}`}
              >
                Sostituisci
              </Button>,
            ]}
          >
            Questo esercizio ha già {progression?.weeks?.length ?? 0} settimane
            configurate.
            <br />
            <strong>Sostituisci</strong> le sovrascrive con la progressione
            scelta; <strong>Aggiungi in coda</strong> le mantiene e accoda le
            nuove settimane a seguire (la progressione diventa personalizzata).
          </Modal>
        </div>
      )}
    </Draggable>
  );
}
